import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from 'sonner';
import { Bell, BellRing, Check, CheckCheck, Trash2, Clock, Play, AlertTriangle } from 'lucide-react';
import { format, parseISO, isAfter } from 'date-fns';
import { fr } from 'date-fns/locale';

const notificationIcons = {
  reminder: Clock,
  start: Play,
  end: Check,
  overdue: AlertTriangle
};

const notificationColors = {
  reminder: 'text-blue-500 bg-blue-500/10',
  start: 'text-green-500 bg-green-500/10',
  end: 'text-gray-500 bg-gray-500/10',
  overdue: 'text-red-500 bg-red-500/10'
};

export default function NotificationCenter() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API}/notifications`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.is_read).length);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  const checkPendingNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API}/notifications/pending`, {
        credentials: 'include'
      });
      if (response.ok) {
        const pending = await response.json();
        pending.forEach(notif => {
          if (!notif.is_read) {
            // Show toast for pending notifications
            const Icon = notificationIcons[notif.type] || Bell;
            toast(notif.message, {
              icon: <Icon className="w-4 h-4" />,
              duration: 10000,
              action: {
                label: 'Marquer lu',
                onClick: () => markAsRead(notif.notification_id)
              }
            });
          }
        });
        // Refresh to get updated state
        if (pending.length > 0) {
          fetchNotifications();
        }
      }
    } catch (error) {
      console.error('Failed to check pending notifications:', error);
    }
  }, [fetchNotifications]);

  useEffect(() => {
    fetchNotifications();
    
    // Check for pending notifications every 30 seconds
    const interval = setInterval(checkPendingNotifications, 30000);
    
    // Initial check
    checkPendingNotifications();
    
    return () => clearInterval(interval);
  }, [fetchNotifications, checkPendingNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: 'PUT',
        credentials: 'include'
      });
      fetchNotifications();
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const formatTriggerTime = (triggerTime) => {
    const date = typeof triggerTime === 'string' ? parseISO(triggerTime) : triggerTime;
    const now = new Date();
    
    if (isAfter(date, now)) {
      return `Prévu ${format(date, "dd/MM à HH:mm", { locale: fr })}`;
    }
    return format(date, "dd/MM à HH:mm", { locale: fr });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative h-9 w-9 p-0"
          data-testid="notification-trigger"
        >
          {unreadCount > 0 ? (
            <BellRing className="w-5 h-5 text-primary animate-pulse" />
          ) : (
            <Bell className="w-5 h-5" />
          )}
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px] bg-primary"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="font-heading font-semibold uppercase tracking-tight text-sm">
            Notifications
          </h4>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs gap-1"
              onClick={markAllAsRead}
              disabled={loading}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-3 h-3" />
              Tout lire
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Bell className="w-10 h-10 text-muted-foreground/50 mb-2" strokeWidth={1} />
              <p className="text-sm text-muted-foreground">Aucune notification</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notif) => {
                const Icon = notificationIcons[notif.type] || Bell;
                const colorClass = notificationColors[notif.type] || 'text-gray-500 bg-gray-500/10';
                
                return (
                  <div 
                    key={notif.notification_id}
                    className={`p-3 hover:bg-secondary/50 transition-colors ${!notif.is_read ? 'bg-primary/5' : ''}`}
                    data-testid={`notification-item-${notif.notification_id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notif.is_read ? 'font-medium' : ''}`}>
                          {notif.message}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 font-mono">
                          {formatTriggerTime(notif.trigger_time)}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1">
                        {!notif.is_read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => markAsRead(notif.notification_id)}
                            title="Marquer comme lu"
                          >
                            <Check className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => deleteNotification(notif.notification_id)}
                          title="Supprimer"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
